import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { SchoolsGateway, SchoolsPage } from '../infrastructure/schools.gateway';
import { pageChanged, pageSizeChanged, schoolsRequested } from './schools.actions';
import { selectPage, selectPageSize } from './schools.selectors';
import { schoolsActions } from './schools.slice';

const LOAD_ERROR = 'Failed to load schools.';

/** Best-effort human message for the failure banner (no domain error union). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : LOAD_ERROR;
}

/**
 * WORKER: fetch the current page. Reads `page` + `pageSize` back from the store
 * (the single source of truth for the fetch) and calls the gateway. `takeLatest`
 * cancels an in-flight fetch when a newer trigger arrives; the abort-latest
 * behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: SchoolsGateway = yield getContext('schoolsGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);

  yield put(schoolsActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchSchools, { page, pageSize })) as SchoolsPage;
    yield put(schoolsActions.loadSucceeded(result));
  } catch (e) {
    yield put(schoolsActions.loadFailed(toMessage(e)));
  }
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(schoolsActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(schoolsActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/** WATCHERS. */
export function* schoolsSaga() {
  yield all([
    takeLatest(schoolsRequested.type, fetchCurrentPage),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
  ]);
}
